import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import { getMyAtktEligibilityAPI, submitAtktFormAPI } from "../utils/Api";
import RazorpayHostedCheckout from "../RazorpayHostedCheckout";

// ATKTBacklogForm — the student-facing ATKT registration.
//
// Replaces the course/batch/pattern dropdowns in ATKTForm.jsx (which the EXAMINER portal
// still uses, deliberately: staff do need to file a form for an arbitrary batch).
//
// WHY: the catalogue is keyed by the year the SUBJECT belongs to, but the old form
// auto-filled `batch` from the student's CURRENT batch. ATKT is by definition a student in
// year N re-attempting a paper from an earlier year, so the two never agreed. A third-year
// LLB student owing "Contract - II" could not find it — it is filed under SYLLB, and TYLLB
// was not in the dropdown at all.
//
// The server already knows what each student owes, so it computes the list and we render
// it. Papers are grouped by the config they belong to because a form is one (course,
// batch, pattern) row and carries its own fee — a student owing papers from two different
// years genuinely files two forms.

// Mirrors computeAmount in controllers/atktController.js and PaymentController.js. Shown
// as an estimate only; the server recomputes it and is authoritative.
const estimateFee = (count) => {
  if (count <= 0) return 0;
  if (count === 1) return 320;
  if (count === 2) return 585;
  return 1255;
};

const ATKTBacklogForm = ({ examSessionId = null, onSubmitted = null }) => {
  const [loading, setLoading] = useState(true);
  const [eligibility, setEligibility] = useState(null);
  const [contactNumber, setContactNumber] = useState("");
  const [selected, setSelected] = useState({}); // configId -> Set(subjectId)
  const [submittingGroup, setSubmittingGroup] = useState(null);
  const [paymentPayload, setPaymentPayload] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("payment_status") === "success") {
      toast.success("Payment completed successfully!");
    }
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await getMyAtktEligibilityAPI(examSessionId);
        if (cancelled) return;
        setEligibility(data);
        setContactNumber(data?.student?.contactNumber || "");
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          "Could not load your outstanding subjects.";
        toast.error(message);
        setEligibility(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [examSessionId]);

  // Memoised so the `totalSelectable` useMemo below does not see a new array identity on
  // every render.
  const groups = useMemo(() => eligibility?.groups || [], [eligibility]);

  const toggleSubject = (configId, subjectId) => {
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[configId] || []);
      if (set.has(subjectId)) set.delete(subjectId);
      else set.add(subjectId);
      next[configId] = set;
      return next;
    });
  };

  const selectedCount = (configId) => (selected[configId]?.size ?? 0);

  // Only one optional paper may be taken per form — the same rule the old form enforced.
  const optionalViolation = (group) => {
    const chosen = [...(selected[group.configId] || [])];
    const optionals = chosen.filter(
      (id) => group.subjects.find((s) => s.id === id)?.group === "optional",
    );
    return optionals.length > 1;
  };

  const handleSubmitGroup = async (group) => {
    const chosen = [...(selected[group.configId] || [])];
    if (!chosen.length) {
      toast.error("Select at least one subject to register for.");
      return;
    }
    if (optionalViolation(group)) {
      toast.error("Only one optional subject can be selected per form.");
      return;
    }
    const phone = String(contactNumber || "").trim();
    if (!/^\+?\d{10,15}$/.test(phone)) {
      toast.error("Enter a valid contact number (10-15 digits).");
      return;
    }

    try {
      setSubmittingGroup(group.configId);
      const resp = await submitAtktFormAPI({
        studentName: eligibility.student.name,
        rollNumber: eligibility.student.rollNumber,
        contactNumber: phone,
        course: group.course,
        batch: group.batch,
        pattern: group.pattern,
        subjects: chosen,
        ...(eligibility?.session?.id ? { examSessionId: eligibility.session.id } : {}),
      });

      if (resp?.order && resp?.key && resp?.callback_url) {
        setPaymentPayload({
          order_id: resp.order.id,
          key_id: resp.key,
          callback_url: resp.callback_url,
          user: resp.user || {},
          notes: { paymentType: "atkt", atktFormId: resp?.data?.formId },
        });
        toast.info("Redirecting to payment gateway...");
      } else {
        toast.success(resp?.message || "A.T.K.T form submitted successfully.");
        if (onSubmitted) onSubmitted();
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Could not submit the ATKT form.",
      );
    } finally {
      setSubmittingGroup(null);
    }
  };

  const totalSelectable = useMemo(
    () =>
      groups.reduce(
        (n, g) => n + g.subjects.filter((s) => !s.alreadyPaid).length,
        0,
      ),
    [groups],
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!eligibility) return null;

  if (!eligibility.canApplyAtkt) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-900">
          ATKT applications are not enabled for your account. Please contact the
          college office.
        </p>
      </div>
    );
  }

  if (!eligibility.registrationOpen) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <p className="text-sm text-gray-700">
          Registration for this exam session is not open at the moment.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-6xl mx-auto">
        {/* Identity — read-only. These come from the student record; a mismatch used to
            be the single most common reason a submission was rejected. */}
        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-5 mb-6">
          <h3 className="text-lg font-semibold mb-4">Your Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                type="text"
                value={eligibility.student.name}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Roll Number
              </label>
              <input
                type="text"
                value={eligibility.student.rollNumber}
                readOnly
                className="mt-1 block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Contact Number
              </label>
              <input
                type="tel"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                placeholder="10-digit mobile number"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
              />
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Current batch: {eligibility.student.batch || "—"} · Course:{" "}
            {eligibility.student.course || "—"}
          </p>
        </div>

        {/* Nothing outstanding */}
        {eligibility.activeBacklogCount === 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-5">
            <h3 className="font-semibold text-green-900 mb-1">
              No outstanding subjects
            </h3>
            <p className="text-sm text-green-800">
              Our records show you have no pending papers, so there is nothing to
              register for. If you believe this is wrong, contact the exam
              section.
            </p>
          </div>
        )}

        {/* Owes something, but nothing offered this session */}
        {eligibility.activeBacklogCount > 0 && groups.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-5 mb-6">
            <h3 className="font-semibold text-amber-900 mb-1">
              Nothing you owe is being examined in this session
            </h3>
            <p className="text-sm text-amber-900">
              You have {eligibility.activeBacklogCount} outstanding subject
              {eligibility.activeBacklogCount === 1 ? "" : "s"}, but none of them
              are offered in this examination session. They are listed below.
            </p>
          </div>
        )}

        {/* The registrable papers, grouped per form */}
        {groups.map((group) => {
          const count = selectedCount(group.configId);
          const fee = estimateFee(count);
          const busy = submittingGroup === group.configId;
          return (
            <div
              key={group.configId}
              className="bg-white rounded-lg shadow-md border border-gray-200 p-5 mb-6"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-1">
                <h3 className="text-lg font-semibold">
                  {group.batchLabel} — {group.pattern}
                </h3>
                <span className="text-xs text-gray-500">
                  {group.course} · {group.subjects.length} paper
                  {group.subjects.length === 1 ? "" : "s"} available to you
                </span>
              </div>
              <p className="text-xs text-gray-500 mb-4">
                These are papers from your {group.batchLabel} year that you have
                still to clear. Registering for a different year is a separate
                form with its own fee.
              </p>

              <div className="space-y-2">
                {group.subjects.map((s) => {
                  const isSelected = selected[group.configId]?.has(s.id);
                  return (
                    <label
                      key={s.id}
                      className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${
                        s.alreadyPaid
                          ? "border-gray-200 bg-gray-50 cursor-not-allowed"
                          : isSelected
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-blue-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-1"
                        disabled={s.alreadyPaid}
                        checked={!!isSelected}
                        onChange={() => toggleSubject(group.configId, s.id)}
                      />
                      <span className="flex-1">
                        <span className="block text-sm font-medium text-gray-800">
                          {s.label}
                          {s.group === "optional" && (
                            <span className="ml-2 text-xs text-gray-500">
                              (optional paper)
                            </span>
                          )}
                        </span>
                        {s.examDate && (
                          <span className="block text-xs text-gray-500 mt-0.5">
                            Exam: {s.examDate}
                            {s.examTime ? ` · ${s.examTime}` : ""}
                          </span>
                        )}
                        {s.alreadyPaid && (
                          <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded bg-green-100 text-green-700">
                            Already paid
                          </span>
                        )}
                      </span>
                    </label>
                  );
                })}
              </div>

              {optionalViolation(group) && (
                <p className="text-sm text-red-600 mt-3">
                  Only one optional paper can be taken per form.
                </p>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-gray-100">
                <div className="text-sm text-gray-700">
                  {count} selected ·{" "}
                  <span className="font-semibold">₹{fee.toFixed(2)}</span>{" "}
                  <span className="text-xs text-gray-500">
                    (confirmed at payment)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleSubmitGroup(group)}
                  disabled={busy || count === 0 || optionalViolation(group)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-5 rounded-md disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {busy ? "Submitting…" : "Proceed to Payment"}
                </button>
              </div>
            </div>
          );
        })}

        {/* Full transparency: papers we could NOT offer, and why. Hiding these would make
            a student think a backlog had vanished. */}
        {(eligibility.notOffered?.length > 0 ||
          eligibility.unresolved?.length > 0) && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5">
            <h3 className="font-semibold text-gray-800 mb-1">
              Not available in this session
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              These outstanding papers are not being examined in this session —
              usually because this session covers different semesters. They will
              appear when a matching session opens.
            </p>
            <ul className="space-y-1">
              {(eligibility.notOffered || []).map((n, i) => (
                <li key={i} className="text-sm text-gray-700">
                  • {n.subjectName}
                  {n.semesterHint ? (
                    <span className="text-xs text-gray-500">
                      {" "}
                      (semester {n.semesterHint})
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
            {eligibility.unresolved?.length > 0 && (
              <p className="text-xs text-gray-600 mt-3">
                {eligibility.unresolved.length} further record
                {eligibility.unresolved.length === 1 ? "" : "s"} could not be
                matched to a subject name. Please contact the exam section so it
                can be reconciled.
              </p>
            )}
          </div>
        )}

        {totalSelectable === 0 && groups.length > 0 && (
          <p className="text-sm text-gray-600">
            You have already paid for every paper available to you in this
            session.
          </p>
        )}
      </div>

      {/* Hidden Razorpay hosted form — props are spread, matching ATKTForm.jsx */}
      {paymentPayload && <RazorpayHostedCheckout {...paymentPayload} />}
    </>
  );
};

export default ATKTBacklogForm;
